/**
 * A pipeline's work now (pipelines.md 2.1): the items as one list grouped by work state in the
 * order of attention (failed, moving, waiting, just landed). What finished more than a few
 * minutes ago is history (the Changes panel's History view), not shown here. One-height rows: the subject with its commit type, where it came
 * from, at most one chip and one time column. A row that changes group fades out where it was
 * (keeping its place until it has gone) and fades in where it lands. Groups and rows never glide:
 * a glide starts from the old place while a new group already holds the new one, which drew a
 * moving row over a header. Every group takes its new place at once, so the space a row arrives
 * in is there before it fades in. Under reduced motion the fades go too. The full story, the
 * workers and the log open in the side panel.
 */
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { pipelineKey } from '@orgus/protocol';
import type { Pipeline, PipelineItem } from '@orgus/protocol';
import { Button, Pressable, StateGroup, Tooltip, cn, formatDuration, formatInt, formatRelative, motionPresets, useNow } from '@orgus/ui';
import { RECENT_ON_BOARD_MS, boardColumns, capacityOwners, itemAge, stageTitle } from './model.ts';
import type { BoardCard } from './model.ts';
import { attemptOf, attemptTip, pipelineChains } from './chains.ts';
import { commitTitle, failureSummary, finishedNote, humanTitle, matchesFilter, queueGroups } from './present.ts';
import { ScopeChip } from './ResultRow.tsx';
import type { BoardFilter } from './present.ts';

/** A finished item's note as a row fact: no label, it says what happened ("Deployed console"). */
function noteFact(note: string | null): { label: null; value: string } | null {
  return note ? { label: null, value: note } : null;
}

/** Finished rows shown before "Show all". */
const FINISHED_ROWS = 5;

/** Whole seconds, at least one (a row that just moved reads "1s", not "708ms"). */
function wholeSeconds(ms: number): string {
  return formatDuration(Math.max(1000, Math.floor(ms / 1000) * 1000));
}

/** Whole minutes once past a minute. */
function wholeMinutes(ms: number): string {
  return ms < 60_000 ? wholeSeconds(ms) : formatDuration(Math.floor(ms / 60_000) * 60_000);
}

/** The cards of what is happening now: in flight, and what finished in the last few minutes. */
export function nowCards(pipeline: Pipeline, now: number): BoardCard[] {
  return boardColumns(pipeline, now)
    .flatMap((c) => c.cards)
    .filter((c) => !c.finished || (c.item.endedAt != null && now - c.item.endedAt < RECENT_ON_BOARD_MS));
}

export function Board({ pipeline, onOpen, activeItemId, filter }: { pipeline: Pipeline; onOpen: (item: PipelineItem) => void; activeItemId?: string | null; filter?: BoardFilter }) {
  const now = useNow(1000);
  const cards = useMemo(() => nowCards(pipeline, now), [pipeline, now]);
  const shown = useMemo(() => (filter && (filter.state || filter.query.trim()) ? cards.filter((c) => matchesFilter(c.item, pipeline, filter, c.note)) : cards), [cards, filter, pipeline]);
  const groups = useMemo(() => queueGroups(shown, pipeline), [shown, pipeline]);
  const chains = useMemo(() => pipelineChains(pipeline), [pipeline]);
  const owners = useMemo(() => capacityOwners(pipeline), [pipeline]);
  // Who sent it, when every row says the same ("orgus land"), is said by none of them.
  const sameBy = useMemo(() => shown.length > 1 && new Set(shown.map((c) => c.item.by ?? '')).size === 1, [shown]);
  const [all, setAll] = useState<Record<string, boolean>>({});
  const key = pipelineKey(pipeline);
  const unit = pipeline.capacity?.label.toLowerCase() ?? 'workers';

  if (!groups.length) {
    return <p className="motion-arrive px-4 py-8 text-center text-ui text-fg-subtle">{filter?.query.trim() ? `No change matches "${filter.query.trim()}"` : 'Nothing in flight'}</p>;
  }

  return (
    <LayoutGroup id={key}>
      <div className="flex flex-col gap-4">
        {groups.map((g) => {
          const active = g.state === 'running' || g.state === 'verifying';
          const finished = g.state === 'landed' || g.state === 'stale' || g.state === 'failed';
          const open = all[g.state] || !finished;
          const rows = open ? g.cards : g.cards.slice(0, FINISHED_ROWS);
          const starts = active ? g.cards.map((c) => c.item.stageStartedAt ?? c.item.startedAt).filter((t): t is number => t != null) : [];
          const oldest = starts.length ? Math.min(...starts) : null;
          // A note every row shares (the batch they verify in) is said once, in the header.
          const notes = new Set(g.cards.map((c) => c.note ?? ''));
          const sharedNote = g.cards.length > 1 && notes.size === 1 ? (g.cards[0]!.note ?? null) : null;
          const held = active ? g.cards.reduce((n, c) => Math.max(n, owners.byItem.get(c.item.id) ?? 0), 0) : 0;
          const meta = [
            active && pipeline.batch && g.cards.length > 1 ? `batch of ${formatInt(g.cards.length)}` : null,
            held ? `${formatInt(held)} ${unit}` : null,
            oldest != null ? wholeMinutes(now - oldest) : null,
            g.state === 'landed' && g.cards[0]?.item.endedAt ? `last ${formatRelative(g.cards[0].item.endedAt, now)}` : null,
          ]
            .filter(Boolean)
            .join(', ');
          return (
            // No layout glide: the group is at its new place the moment the list changes.
            <div key={g.state}>
            <StateGroup state={g.state} label={g.label} count={g.cards.length} meta={meta || undefined} className="motion-arrive">
              <ul className="flex flex-col" aria-label={g.label}>
                {/* Sync, not popLayout: a leaving row keeps its height until it has faded, so nothing slides under it. Opacity only, no offset. */}
                <AnimatePresence initial={false}>
                  {rows.map((c) => (
                    <motion.li key={c.item.id} initial={{ opacity: 0 }} animate={{ opacity: 1, transition: motionPresets.arrive.animate.transition }} exit={motionPresets.arrive.exit}>
                      <ItemRow card={c} pipeline={pipeline} now={now} onOpen={onOpen} selected={c.item.id === activeItemId} attempt={attemptOf(chains, c.item.id)} showStage={g.mixedStages} hideNote={sharedNote != null} hideBy={sameBy} leadless={!g.cards.some((x) => x.position != null)} />
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>
              {g.cards.length > rows.length && (
                // Laid out as a row (lead slot, then text), so the label starts under the titles above it.
                <div className="flex items-center gap-3 px-row-x">
                  <span className="w-4 shrink-0" aria-hidden />
                  <Button variant="ghost" size="sm" className="-ml-2 text-fg-subtle" onClick={() => setAll((x) => ({ ...x, [g.state]: true }))}>
                    Show all {formatInt(g.cards.length)}
                  </Button>
                </div>
              )}
            </StateGroup>
            </div>
          );
        })}
      </div>
    </LayoutGroup>
  );
}

/**
 * One item on one line, in the result rows' anatomy: its place in the queue (queued only) in the
 * lead column, the subject in plain words, its commit scope as the one small mono chip, then a
 * dim caption (priority or attempt, where it came from, its note) and one time column.
 */
function ItemRow({
  card,
  pipeline,
  now,
  onOpen,
  selected,
  attempt,
  showStage,
  hideNote,
  hideBy,
  leadless,
}: {
  card: BoardCard;
  pipeline: Pipeline;
  now: number;
  onOpen: (i: PipelineItem) => void;
  selected: boolean;
  attempt: ReturnType<typeof attemptOf>;
  showStage: boolean;
  hideNote: boolean;
  hideBy: boolean;
  /** No row of its group has a queue place: on a phone the empty lead column gives its width to the title. */
  leadless: boolean;
}) {
  const { item, note, position } = card;
  const active = item.state === 'active';
  const human = humanTitle(item);
  const t = commitTitle(human.title);
  const retry = attempt && attempt.n > 1 ? attempt : null;
  const flag = item.priority ? { label: 'Priority', tip: 'Goes before older items' } : retry ? { label: `Attempt ${formatInt(retry.n)}`, tip: attemptTip(retry.chain, retry.n) } : null;
  const age = itemAge(item, now, active ? wholeSeconds : wholeMinutes);
  const time = item.endedAt != null ? formatRelative(item.endedAt, now) : age ? (active ? wholeSeconds(age.stage) : wholeMinutes(age.stage)) : '';
  const failed = item.state === 'failed';
  // A failure reads as a short status and the one thing that failed; the whole line is in the drawer.
  const failure = failed ? failureSummary(item, pipeline) : null;
  // The row budget: one chip and at most two meta facts, the flag and a failure's status counting
  // as one each. A queued row's place is its lead number, and the batch it verifies in is in the
  // group header and the drawer, so neither repeats here.
  const room = 2 - (flag ? 1 : 0) - (failure ? 1 : 0);
  // Each fact carries its tiny label where its value alone does not say what it is: a worktree
  // or checkout name ("qawl-wt-chat", "Qawl-Agents") reads "From qawl-wt-chat".
  const facts = [
    showStage ? { label: null, value: stageTitle(pipeline, item.stage) } : null,
    failure ? (failure.reason ? { label: null, value: failure.reason } : null) : card.finished && !hideNote ? noteFact(finishedNote(item)) : null,
    hideBy || !item.by ? null : { label: 'From', value: item.by },
  ]
    .filter((f): f is { label: string | null; value: string } => !!f && !!f.value)
    .slice(0, Math.max(0, room));
  const factsText = facts.map((f) => (f.label ? `${f.label} ${f.value}` : f.value)).join(', ');
  const fullFacts = [card.finished ? null : note, item.by].filter(Boolean).join(', ');

  return (
    <div className="relative">
      {selected && (
        <motion.span layoutId={`${pipelineKey(pipeline)}:selected`} transition={motionPresets.move} className="selected-tint absolute inset-0 rounded-control" aria-hidden />
      )}
      <Pressable
        data-row=""
        onClick={() => onOpen(item)}
        aria-current={selected || undefined}
        title={fullFacts || undefined}
        // On a phone the title may take two lines rather than be cut after a few words.
        className={cn('relative flex h-row items-center gap-3 rounded-control px-row-x max-md:h-auto max-md:min-h-row-2line max-md:px-2 max-md:py-2', !selected && 'hover:bg-hover')}
      >
        {/* The queue place is the row's lead: in the 16 px icon slot, under the group header's icon. Empty for the whole group, on a phone it gives its width to the title. */}
        <span className={cn('flex w-4 shrink-0 justify-center text-caption text-fg-subtle num', leadless && 'max-md:hidden')}>{position != null ? formatInt(position) : ''}</span>
        <span className="flex min-w-0 flex-1 items-center gap-3">
          <span className={cn('min-w-0 truncate text-ui font-medium max-md:line-clamp-2 max-md:whitespace-normal md:max-w-3/5', human.id ? 'text-fg-muted' : 'text-fg')} title={item.title}>
            {t.subject}
          </span>
          <ScopeChip type={t.type} />
          {(flag || facts.length > 0 || failure) && (
            <span className="hidden min-w-0 flex-1 items-center gap-2 text-caption text-fg-subtle md:flex" data-meta="">
              {failure && <span className="shrink-0 font-medium text-status-danger-ink">{failure.label}</span>}
              {flag && (
                <Tooltip content={flag.tip}>
                  <span tabIndex={-1} className="shrink-0 text-fg-muted">
                    {flag.label}
                  </span>
                </Tooltip>
              )}
              {facts.map((f, i) => (
                <span key={i} className={cn('min-w-0 truncate', i < facts.length - 1 && 'shrink-0')} title={factsText}>
                  {f.label && <span>{f.label} </span>}
                  <span className={f.label ? 'text-fg-muted' : undefined}>{f.value}</span>
                </span>
              ))}
            </span>
          )}
        </span>
        <span className="w-16 shrink-0 text-right text-caption text-fg-subtle num" title={age?.label}>
          {time}
        </span>
      </Pressable>
    </div>
  );
}
